import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Plus, Edit, Briefcase, Users } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { omAPI, hcmAPI } from '../../../services/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Card } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';

const Jobs = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('jobs');
  const [viewItem, setViewItem] = useState(null);
  const navigate = useNavigate();
  
  const [jobs, setJobs] = useState([]);
  const [payScaleTypes, setPayScaleTypes] = useState([]);
  const [payScaleAreas, setPayScaleAreas] = useState([]);
  const [payScaleGroups, setPayScaleGroups] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formResetCounter, setFormResetCounter] = useState(0);
  const [filterFamily, setFilterFamily] = useState('');
  
  // Form state
  const [selectedPayScaleType, setSelectedPayScaleType] = useState('');
  const [selectedPayScaleArea, setSelectedPayScaleArea] = useState('');
  const [selectedPayScaleGroup, setSelectedPayScaleGroup] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Active');
  const [selectedJobFamily, setSelectedJobFamily] = useState('');

  const jobFamilies = [
    'Engineering', 'Human Resources', 'Finance', 'Sales', 'Marketing', 
    'Operations', 'IT', 'Legal', 'Administration', 'Research', 'Other'
  ];

  const loadMasterData = useCallback(async () => {
    try {
      const pstRes = await hcmAPI.getPayScaleTypes();
      setPayScaleTypes(pstRes.data || []);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const params = filterFamily ? { job_family: filterFamily } : {};
      const res = await omAPI.getJobs(params);
      setJobs(res.data || []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      toast.error('Failed to load jobs');
    }
  }, [filterFamily]);

  useEffect(() => {
    loadMasterData();
  }, [loadMasterData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadPayScaleAreas = async (typeId) => {
    if (!typeId) {
      setPayScaleAreas([]);
      return;
    }
    try {
      const res = await hcmAPI.getPayScaleAreas(typeId);
      setPayScaleAreas(res.data || []);
    } catch (error) {
      setPayScaleAreas([]);
    }
  };

  const loadPayScaleGroups = async (typeId, areaId) => {
    if (!typeId || !areaId) {
      setPayScaleGroups([]);
      return;
    }
    try {
      const res = await hcmAPI.getPayScaleGroups(typeId, areaId);
      setPayScaleGroups(res.data || []);
    } catch (error) {
      setPayScaleGroups([]);
    }
  };

  const handlePayScaleTypeChange = (value) => {
    setSelectedPayScaleType(value);
    setSelectedPayScaleArea('');
    setSelectedPayScaleGroup('');
    setPayScaleGroups([]);
    loadPayScaleAreas(value === 'none' ? '' : value);
  };

  const handlePayScaleAreaChange = (value) => {
    setSelectedPayScaleArea(value);
    setSelectedPayScaleGroup('');
    loadPayScaleGroups(selectedPayScaleType, value === 'none' ? '' : value);
  };

  const resetFormState = () => {
    setSelectedPayScaleType('');
    setSelectedPayScaleArea('');
    setSelectedPayScaleGroup('');
    setSelectedStatus('Active');
    setSelectedJobFamily('');
    setPayScaleAreas([]);
    setPayScaleGroups([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Clean up empty strings
    Object.keys(data).forEach(key => {
      if (data[key] === '' || data[key] === 'none') {
        delete data[key];
      }
    });

    // Convert min_experience_years to number
    if (data.min_experience_years) {
      data.min_experience_years = parseInt(data.min_experience_years, 10);
    }

    try {
      if (editingItem) {
        await omAPI.updateJob(editingItem.job_id, data);
        toast.success('Job updated successfully');
      } else {
        await omAPI.createJob(data);
        toast.success('Job created successfully');
      }
      
      setIsDialogOpen(false);
      setEditingItem(null);
      resetFormState();
      setFormResetCounter(prev => prev + 1);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Operation failed');
      }
    }
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    setSelectedStatus(item.status || 'Active');
    setSelectedJobFamily(item.job_family || '');
    
    if (item.pay_scale_type_id) {
      setSelectedPayScaleType(item.pay_scale_type_id);
      await loadPayScaleAreas(item.pay_scale_type_id);
      
      if (item.pay_scale_area_id) {
        setSelectedPayScaleArea(item.pay_scale_area_id);
        await loadPayScaleGroups(item.pay_scale_type_id, item.pay_scale_area_id);
        
        if (item.pay_scale_group_id) {
          setSelectedPayScaleGroup(item.pay_scale_group_id);
        }
      }
    }
    
    setIsDialogOpen(true);
    setFormResetCounter(prev => prev + 1);
  };

  return (
    <Layout>
      <div className="p-6" data-testid="jobs-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/om')}>Org Management</span>
            <span>/</span>
            <span>Jobs</span>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Jobs</h1>
              <p className="text-gray-600 mt-1">Manage job classifications and role templates</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 flex gap-4">
          <Select value={filterFamily || "all"} onValueChange={(val) => setFilterFamily(val === "all" ? "" : val)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by Job Family" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Families</SelectItem>
              {jobFamilies.map(family => (
                <SelectItem key={family} value={family}>{family}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Content Card */}
        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <div className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-gray-500" />
              <h2 className="font-semibold text-gray-900">Jobs</h2>
              <span className="text-sm text-gray-500">({jobs.length} total)</span>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setEditingItem(null);
                resetFormState();
              }
            }}>
              <DialogTrigger asChild>
                {writable && (<Button size="sm" data-testid="create-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Create Job
                </Button>)}
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingItem ? 'Edit' : 'Create'} Job</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} key={formResetCounter} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="job_id">Job ID *</Label>
                      <Input id="job_id" name="job_id" placeholder="e.g., JOB001, SE01" maxLength={20} defaultValue={editingItem?.job_id || ''} disabled={!!editingItem} required />
                    </div>
                    <div>
                      <Label htmlFor="name">Job Title *</Label>
                      <Input id="name" name="name" placeholder="e.g., Software Engineer" maxLength={100} defaultValue={editingItem?.name || ''} required />
                    </div>
                    <div>
                      <Label htmlFor="short_name">Short Name</Label>
                      <Input id="short_name" name="short_name" placeholder="e.g., SE" maxLength={20} defaultValue={editingItem?.short_name || ''} />
                    </div>
                    <div>
                      <Label htmlFor="job_family">Job Family</Label>
                      <Select value={selectedJobFamily} onValueChange={setSelectedJobFamily}>
                        <SelectTrigger><SelectValue placeholder="Select Job Family" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {jobFamilies.map(family => (
                            <SelectItem key={family} value={family}>{family}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="job_family" value={selectedJobFamily === 'none' ? '' : selectedJobFamily} />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="description">Description</Label>
                      <textarea 
                        id="description" 
                        name="description" 
                        placeholder="Job description and responsibilities" 
                        maxLength={1000} 
                        defaultValue={editingItem?.description || ''} 
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm min-h-[80px]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="job_grade">Job Grade</Label>
                      <Input id="job_grade" name="job_grade" placeholder="e.g., L1, L2, Senior" maxLength={20} defaultValue={editingItem?.job_grade || ''} />
                    </div>
                    <div>
                      <Label htmlFor="min_experience_years">Min Experience (Years)</Label>
                      <Input type="number" id="min_experience_years" name="min_experience_years" min="0" max="50" defaultValue={editingItem?.min_experience_years || ''} />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="education_requirement">Education Requirement</Label>
                      <Input id="education_requirement" name="education_requirement" placeholder="e.g., Bachelor's in Computer Science" maxLength={100} defaultValue={editingItem?.education_requirement || ''} />
                    </div>
                    
                    {/* Pay Scale Section */}
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Pay Scale Association (Optional)</h3>
                    </div>
                    <div>
                      <Label htmlFor="pay_scale_type_id">Pay Scale Type</Label>
                      <Select value={selectedPayScaleType} onValueChange={handlePayScaleTypeChange}>
                        <SelectTrigger><SelectValue placeholder="Select Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {payScaleTypes.filter(t => t.status === 'Active').map(t => (
                            <SelectItem key={t.pay_scale_type_id} value={t.pay_scale_type_id}>{t.pay_scale_type_id} - {t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="pay_scale_type_id" value={selectedPayScaleType === 'none' ? '' : selectedPayScaleType} />
                    </div>
                    <div>
                      <Label htmlFor="pay_scale_area_id">Pay Scale Area</Label>
                      <Select value={selectedPayScaleArea} onValueChange={handlePayScaleAreaChange} disabled={!selectedPayScaleType || selectedPayScaleType === 'none'}>
                        <SelectTrigger><SelectValue placeholder="Select Area" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {payScaleAreas.filter(a => a.status === 'Active').map(a => (
                            <SelectItem key={a.pay_scale_area_id} value={a.pay_scale_area_id}>{a.pay_scale_area_id} - {a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="pay_scale_area_id" value={selectedPayScaleArea === 'none' ? '' : selectedPayScaleArea} />
                    </div>
                    <div>
                      <Label htmlFor="pay_scale_group_id">Pay Scale Group</Label>
                      <Select value={selectedPayScaleGroup} onValueChange={setSelectedPayScaleGroup} disabled={!selectedPayScaleArea || selectedPayScaleArea === 'none'}>
                        <SelectTrigger><SelectValue placeholder="Select Group" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {payScaleGroups.filter(g => g.status === 'Active').map(g => (
                            <SelectItem key={g.pay_scale_group_id} value={g.pay_scale_group_id}>{g.pay_scale_group_id} - {g.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="pay_scale_group_id" value={selectedPayScaleGroup === 'none' ? '' : selectedPayScaleGroup} />
                    </div>
                    
                    <div>
                      <Label htmlFor="status">Status *</Label>
                      <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                        <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Blocked">Blocked</SelectItem>
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="status" value={selectedStatus} />
                    </div>
                    <div>
                      <Label htmlFor="valid_from">Valid From *</Label>
                      <Input type="date" id="valid_from" name="valid_from" defaultValue={editingItem?.valid_from || new Date().toISOString().split('T')[0]} required />
                    </div>
                    <div>
                      <Label htmlFor="valid_to">Valid To</Label>
                      <Input type="date" id="valid_to" name="valid_to" defaultValue={editingItem?.valid_to || ''} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-btn">{editingItem ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Job Title</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Family</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Grade</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Min Exp</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Positions</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length > 0 ? (
                  jobs.map((item) => (
                    <tr key={item.job_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm font-semibold">{item.job_id}</td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium">{item.name}</div>
                        {item.short_name && <div className="text-xs text-gray-500">{item.short_name}</div>}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {item.job_family ? (
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">{item.job_family}</span>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm">{item.job_grade || '-'}</td>
                      <td className="py-3 px-4 text-sm">{item.min_experience_years != null ? `${item.min_experience_years} yrs` : '-'}</td>
                      <td className="py-3 px-4">
                        <span className="flex items-center gap-1 text-sm">
                          <Users className="h-4 w-4 text-gray-400" />
                          {item.position_count || 0}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <RecordActions doctype="jobs" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-500">
                      No jobs found. Click "Create Job" to add one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default Jobs;
